import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { Share2, ChevronLeft, Check } from 'lucide-react';

type RewardModel = 'coupon' | 'cash' | 'none';
type RewardTrigger = 'install' | 'signup' | 'qualified_action' | 'paid';

interface FormState {
  name: string;
  slug: string;
  description: string;
  channel: string;
  reward_model: RewardModel;
  reward_trigger: RewardTrigger;
  referrer_reward_dollars: string;
  status: 'draft' | 'active';
}

// Enforced taxonomy (mig 285) — dropdown, never free text: the anti-"IG vs instagram" guard.
const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'influencer', label: 'Influencer' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'bio_link', label: 'Bio link (IG/TikTok/FB profile)' },
  { value: 'press', label: 'Press / PR' },
  { value: 'offline_qr', label: 'Offline / QR (flyers, events)' },
  { value: 'email', label: 'Email campaign' },
  { value: 'paid_ads', label: 'Paid ads' },
  { value: 'other', label: 'Other' },
];

const REWARD_MODEL_OPTIONS: { value: RewardModel; label: string; description: string }[] = [
  { value: 'coupon', label: 'Coupon', description: 'Referrer/referee get an in-app coupon (store-compliant).' },
  { value: 'cash', label: 'Cash (ledger)', description: 'Cash reward tracked in the ledger; payout is manual/off-app.' },
  { value: 'none', label: 'None', description: 'Track referrals only, no automatic reward.' },
];

const TRIGGER_OPTIONS: { value: RewardTrigger; label: string; description: string }[] = [
  { value: 'install', label: 'Install', description: 'Reward as soon as the app is installed (weakest signal).' },
  { value: 'signup', label: 'Signup', description: 'Reward when the referred user creates an account.' },
  { value: 'qualified_action', label: 'Qualified action', description: 'Reward on a real milestone (recommended — anti-fraud).' },
  { value: 'paid', label: 'Paid', description: 'Reward only when the referred user pays.' },
];

function slugify(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function ReferralCampaignCreatePage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const [form, setForm] = useState<FormState>({
    name: '',
    slug: '',
    description: '',
    channel: 'influencer',
    reward_model: 'coupon',
    reward_trigger: 'qualified_action',
    referrer_reward_dollars: '',
    status: 'draft',
  });

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const effectiveSlug = slugTouched ? slugify(form.slug) : slugify(form.name);
  const valid = form.name.trim().length > 0 && effectiveSlug.length > 0;

  const handleSubmit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const idToken = await getIdToken();
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        slug: effectiveSlug,
        channel: form.channel,
        reward_model: form.reward_model,
        reward_trigger: form.reward_trigger,
        status: form.status,
      };
      if (form.description.trim()) body.description = form.description.trim();
      if (form.reward_model === 'cash' && form.referrer_reward_dollars.trim()) {
        body.referrer_reward_cents = Math.round(Number(form.referrer_reward_dollars) * 100);
      }

      const r = await fetch(`${apiUrl}/v1/admin/referrals/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((err as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      const created = (await r.json()) as { id: string; name: string; status: string };
      toast({ title: 'Campaign created', description: `${created.name} (${created.status})` });
      navigate(`/referrals/${created.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({ title: 'Create failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border rounded-md text-sm bg-background';

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/referrals')}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to referrals
        </button>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Share2 className="w-6 h-6" />
          New referral campaign
        </h1>
      </div>

      <div className="space-y-5 border rounded-lg p-6 bg-card">
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <input
            className={inputCls}
            value={form.name}
            maxLength={120}
            placeholder="e.g. Summer Influencer Push"
            onChange={(e) => update('name', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Slug</label>
          <input
            className={inputCls}
            value={effectiveSlug}
            placeholder="auto-generated from name"
            onChange={(e) => {
              setSlugTouched(true);
              update('slug', e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground mt-1">
            URL-safe identifier, unique per environment.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            className={inputCls}
            rows={2}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Channel</label>
          <select
            className={inputCls}
            value={form.channel}
            onChange={(e) => update('channel', e.target.value)}
          >
            {CHANNEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Which acquisition channel this campaign tracks — powers the per-channel dashboard.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Reward model</label>
          <div className="grid gap-2">
            {REWARD_MODEL_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => update('reward_model', o.value)}
                className={`text-left px-4 py-3 border rounded-md transition ${
                  form.reward_model === o.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/30'
                }`}
              >
                <div className="font-medium text-sm">{o.label}</div>
                <div className="text-xs text-muted-foreground">{o.description}</div>
              </button>
            ))}
          </div>
        </div>

        {form.reward_model === 'cash' && (
          <div>
            <label className="block text-sm font-medium mb-1">Referrer cash reward (USD)</label>
            <input
              className={inputCls}
              type="number"
              min="0"
              step="0.01"
              value={form.referrer_reward_dollars}
              placeholder="e.g. 10.00"
              onChange={(e) => update('referrer_reward_dollars', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Recorded in the reward ledger. Payout itself is manual/off-app (no automatic disbursement).
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Reward trigger</label>
          <div className="grid gap-2">
            {TRIGGER_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => update('reward_trigger', o.value)}
                className={`text-left px-4 py-3 border rounded-md transition ${
                  form.reward_trigger === o.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/30'
                }`}
              >
                <div className="font-medium text-sm">{o.label}</div>
                <div className="text-xs text-muted-foreground">{o.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Initial status</label>
          <select
            className={inputCls}
            value={form.status}
            onChange={(e) => update('status', e.target.value as 'draft' | 'active')}
          >
            <option value="draft">Draft (not live)</option>
            <option value="active">Active</option>
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate('/referrals')}
            className="px-4 py-2 text-sm border rounded-md hover:bg-muted/30"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!valid || submitting}
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            {submitting ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
